import { motion } from "framer-motion";
import { CheckCircle2, MessageCircle, UserRoundCheck } from "lucide-react";
import { Reveal, SectionHead, EASE } from "./Reveal";

const VALUES = [
  {
    title: "Continuità",
    desc: "Non devi spiegare ogni volta tutto da capo. Il professionista può conoscere la tua posizione e seguirne l'evoluzione.",
  },
  {
    title: "Competenza",
    desc: "Le risposte non sono generiche, ma tengono conto della tua attività e delle tue esigenze.",
  },
  {
    title: "Relazione diretta",
    desc: "Quando hai bisogno di assistenza, sai a chi rivolgerti e puoi farlo direttamente attraverso Human Factor.",
  },
  {
    title: "Linguaggio comprensibile",
    desc: "Ti aiutiamo a capire che cosa sta succedendo, che cosa devi fare e perché, senza complicazioni inutili.",
  },
];

export default function Professionista() {
  return (
    <section
      id="professionista"
      data-testid="section-il-professionista"
      className="relative border-y border-white/5 bg-slate-950/40 py-24 lg:py-32"
    >
      <div className="pointer-events-none absolute bottom-[10%] left-[30%] h-[380px] w-[380px] rounded-full bg-[#1B6EF3]/10 blur-[130px]" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <div className="relative order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: -2 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="relative"
          >
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-[#1B6EF3]/50 via-transparent to-[#19D6E8]/50 opacity-60 blur-xl" />
            <div className="relative overflow-hidden rounded-[1.75rem] border border-white/15">
              <img
                src="https://images.unsplash.com/photo-1676288869178-1761f567ea91?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Il professionista Human Factor"
                loading="lazy"
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/95 via-transparent to-transparent" />
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(25,214,232,0.18),transparent_55%)]" />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="absolute -right-3 top-8 flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-900/90 px-4 py-3 shadow-2xl shadow-black/50 backdrop-blur-xl sm:-right-6"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300">
                <UserRoundCheck className="h-4 w-4" />
              </span>
              <div>
                <p className="text-xs font-semibold text-white">Professionisti interni</p>
                <p className="font-mono text-[10px] text-slate-400">Parte della nostra struttura</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="animate-float absolute -left-3 bottom-10 flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-900/90 px-4 py-3 shadow-2xl shadow-black/50 backdrop-blur-xl sm:-left-6"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1B6EF3]/20 text-blue-300">
                <MessageCircle className="h-4 w-4" />
              </span>
              <div>
                <p className="text-xs font-semibold text-white">Nessun rimbalzo</p>
                <p className="font-mono text-[10px] text-slate-400">Niente call center, niente fornitori esterni</p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <div className="order-1 flex flex-col gap-8 lg:order-2">
          <SectionHead
            eyebrow="Il fattore umano"
            title={
              <>
                La tecnologia semplifica. <span className="text-gradient">Le persone fanno la differenza.</span>
              </>
            }
            sub="Dietro l'applicazione ci sono professionisti veri. E sono parte della nostra struttura."
          />
          <Reveal delay={0.15}>
            <div className="max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
              <p>
                Human Factor non è una piattaforma che ti lascia solo davanti a dati, notifiche e procedure. È un
                servizio gestito direttamente da professionisti interni, che lavorano in connessione con la piattaforma
                e possono seguire la tua posizione con continuità.
              </p>
              <p className="mt-4">
                Nessun rimbalzo tra applicazione, call center e fornitori esterni. Hai un canale diretto con chi
                conosce la tua situazione e può aiutarti a comprenderla e gestirla.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
                className="glow-card flex flex-col gap-2 rounded-2xl p-5"
                data-testid={`professionista-valore-${i}`}
              >
                <span className="flex items-center gap-2 font-display text-base font-bold text-white">
                  <CheckCircle2 className="h-4 w-4 text-cyan-300" />
                  {v.title}
                </span>
                <p className="text-xs leading-relaxed text-slate-400 sm:text-sm">{v.desc}</p>
              </motion.div>
            ))}
          </div>
          <Reveal delay={0.3}>
            <p className="max-w-xl font-display text-lg font-bold leading-snug sm:text-xl">
              Non soltanto un'app. Non soltanto un professionista.{" "}
              <span className="text-gradient">Un unico servizio che unisce entrambi.</span>
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
